/** Shared time-scale helpers for lightweight-charts (price + volume panels). */

export const CHART_ZOOM_RANGES = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: '2Y', days: 730 },
  { label: '5Y', days: 1825 },
  { label: 'All', days: null },
];

export function isValidVisibleRange(range) {
  if (!range || range.from == null || range.to == null) return false;
  if (typeof range.from === 'string') {
    const from = String(range.from);
    const to = String(range.to);
    return from.length >= 10 && to.length >= 10 && from < to;
  }
  return Number.isFinite(range.from) && Number.isFinite(range.to) && range.from < range.to;
}

export function normalizeBarDay(time) {
  if (time == null) return null;
  return String(time).slice(0, 10);
}

/** Keep volume/OBV bars on the same calendar as the price series. */
export function filterSignalBarsForPriceBars(signalBars, priceBars) {
  if (!priceBars?.length) return signalBars || [];
  const keys = new Set(priceBars.map((b) => normalizeBarDay(b.time)));
  return (signalBars || []).filter((b) => keys.has(normalizeBarDay(b.time)));
}

export function applyVisibleRange(
  chart,
  barData,
  rangeLabel,
  zoomRanges = CHART_ZOOM_RANGES,
) {
  if (!chart || !barData?.length) return;

  const spec = zoomRanges.find((r) => r.label === rangeLabel);
  if (!spec || spec.days == null) {
    chart.timeScale().fitContent();
    return;
  }

  const to = normalizeBarDay(barData[barData.length - 1].time);
  const end = new Date(`${to}T00:00:00Z`);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - spec.days);
  const from = start.toISOString().slice(0, 10);

  try {
    chart.timeScale().setVisibleRange({ from, to });
  } catch {
    chart.timeScale().fitContent();
  }
}

export function copyVisibleTimeRange(fromChart, toChart) {
  if (!fromChart || !toChart) return;
  let range;
  try {
    range = fromChart.timeScale().getVisibleRange();
  } catch {
    return;
  }
  if (!isValidVisibleRange(range)) return;
  try {
    toChart.timeScale().setVisibleRange(range);
  } catch {
    /* resize / empty series */
  }
}

/** Bidirectional visible **time** sync (not logical bar index). */
export function syncChartsVisibleTimeRange(sourceChart, targetChart) {
  if (!sourceChart || !targetChart) return () => {};

  let syncFromSource = false;
  let syncFromTarget = false;

  const pushRange = (target, range) => {
    if (!isValidVisibleRange(range)) return;
    try {
      target.timeScale().setVisibleRange(range);
    } catch {
      /* ignore sync glitches during resize / empty series */
    }
  };

  const onSource = (range) => {
    if (syncFromTarget) return;
    syncFromSource = true;
    pushRange(targetChart, range);
    syncFromSource = false;
  };

  const onTarget = (range) => {
    if (syncFromSource) return;
    syncFromTarget = true;
    pushRange(sourceChart, range);
    syncFromTarget = false;
  };

  sourceChart.timeScale().subscribeVisibleTimeRangeChange(onSource);
  targetChart.timeScale().subscribeVisibleTimeRangeChange(onTarget);

  return () => {
    sourceChart.timeScale().unsubscribeVisibleTimeRangeChange(onSource);
    targetChart.timeScale().unsubscribeVisibleTimeRangeChange(onTarget);
  };
}
